import {useMutation} from '@apollo/react-hooks';

import type {UpdateAction} from 'shells/inbox/types';

import type {
    ArchiveThread as ArchiveThreadMutation,
    ArchiveThreadVariables as ArchiveThreadMutationVariables,
    CloseThread as CloseThreadMutation,
    CloseThreadVariables as CloseThreadMutationVariables,
    ReopenThread as ReopenThreadMutation,
    ReopenThreadVariables as ReopenThreadMutationVariables,
} from 'nutshell-graphql-types';

import INBOX_THREAD_FRAGMENT from '../fragments/inbox-thread-fragment.graphql';
import ARCHIVE_THREAD from '../mutations/archive-thread.graphql';
import CLOSE_THREAD from '../mutations/close-thread.graphql';
import REOPEN_THREAD from '../mutations/reopen-thread.graphql';

export function useUpdateThreadStatus(): {
    updateThreadStatus: (threadId: string, updateAction: UpdateAction) => Promise<any>;
} {
    const [archiveThread] = useMutation<ArchiveThreadMutation, ArchiveThreadMutationVariables>(
        ARCHIVE_THREAD
    );
    const [closeThread] = useMutation<CloseThreadMutation, CloseThreadMutationVariables>(
        CLOSE_THREAD
    );
    const [reopenThread] = useMutation<ReopenThreadMutation, ReopenThreadMutationVariables>(
        REOPEN_THREAD
    );

    return {
        updateThreadStatus: (threadId: string, updateAction: UpdateAction) => {
            if (updateAction === 'ARCHIVE') {
                return archiveThread({
                    variables: {input: {threadId}},
                    refetchQueries: ['GetInboxThreadCounts'],
                    update: (cache, {data}) => {
                        const cacheResult = cache.readFragment({
                            id: threadId,
                            fragmentName: 'InboxThreadFragment',
                            fragment: INBOX_THREAD_FRAGMENT,
                        });

                        if (cacheResult && data) {
                            cache.writeFragment({
                                id: threadId,
                                fragmentName: 'InboxThreadFragment',
                                fragment: INBOX_THREAD_FRAGMENT,
                                data: {...cacheResult, effectiveStatus: 'ARCHIVED'},
                            });
                        }
                    },
                });
            } else if (updateAction === 'CLOSE') {
                return closeThread({
                    variables: {input: {threadId}},
                    update: (cache, {data}) => {
                        const cacheResult = cache.readFragment({
                            id: threadId,
                            fragmentName: 'InboxThreadFragment',
                            fragment: INBOX_THREAD_FRAGMENT,
                        });

                        if (cacheResult && data) {
                            cache.writeFragment({
                                id: threadId,
                                fragmentName: 'InboxThreadFragment',
                                fragment: INBOX_THREAD_FRAGMENT,
                                data: {...cacheResult, effectiveStatus: 'CLOSED'},
                            });
                        }
                    },
                });
            } else {
                return reopenThread({
                    variables: {input: {threadId}},
                    update: (cache, {data}) => {
                        const cacheResult = cache.readFragment({
                            id: threadId,
                            fragmentName: 'InboxThreadFragment',
                            fragment: INBOX_THREAD_FRAGMENT,
                        });

                        if (cacheResult && data) {
                            cache.writeFragment({
                                id: threadId,
                                fragmentName: 'InboxThreadFragment',
                                fragment: INBOX_THREAD_FRAGMENT,
                                data: {...cacheResult, effectiveStatus: 'OPEN'},
                            });
                        }
                    },
                });
            }
        },
    };
}
